import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventsAPI } from '@/services';
import { useToast } from '../components/Toast';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { getResolvedPortalTitle } from '../utils/portalBranding';

const PublicEventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { error, success } = useToast();
  const { isAuthenticated } = useAuth();
  const { settings } = useSettings();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentProofPreview, setPaymentProofPreview] = useState(null);
  
  const paymentMethods = settings?.financial?.payment_methods || ['banque', 'mobile_money', 'espèces', 'virement', 'chèque'];
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const [registrationData, setRegistrationData] = useState({
    name: '',
    first_name: '',
    whatsapp: '',
    payment_method: '',
    payment_proof_url: ''
  });

  useEffect(() => {
    loadEventDetails();
  }, [id]);

  const loadEventDetails = async () => {
    try {
      setLoading(true);
      // Utiliser l'API sans authentification pour les événements publics
      const eventResponse = await eventsAPI.getPublicById(id);
      setEvent(eventResponse.data);
    } catch (err) {
      console.error('Failed to load event details:', err);
      error(err.response?.data?.detail || err.message || 'Erreur lors du chargement de l\'événement');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentProofUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    const fileExt = file.name.toLowerCase().split('.').pop();
    const allowedTypes = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'];
    if (!allowedTypes.includes(fileExt)) {
      error('Type de fichier non autorisé. Utilisez une image (JPG, PNG, GIF, WebP) ou un PDF.');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      error('Le fichier est trop volumineux. Taille maximale: 10MB.');
      return;
    }
    
    setUploadingProof(true);
    try {
      const response = await eventsAPI.uploadPaymentProof(id, file);
      setRegistrationData({ ...registrationData, payment_proof_url: response.data.file_url });
      setPaymentProofFile(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPaymentProofPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        setPaymentProofPreview(null);
      }
      
      success('Preuve de paiement uploadée avec succès');
    } catch (err) {
      console.error('Upload error:', err);
      error(err.response?.data?.detail || 'Erreur lors de l\'upload de la preuve de paiement');
    } finally {
      setUploadingProof(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!event.is_public) {
      error('Cet événement est réservé aux membres. Veuillez vous connecter.');
      navigate('/login');
      return;
    }

    try {
      setRegistering(true);
      await eventsAPI.registerPublic(id, registrationData);
      success('Inscription réussie ! Vous recevrez une confirmation par WhatsApp.');
      setShowRegistrationForm(false);
      setRegistrationData({
        name: '',
        first_name: '',
        whatsapp: '',
        payment_method: '',
        payment_proof_url: ''
      });
      setPaymentProofFile(null);
      setPaymentProofPreview(null);
      // Recharger les détails de l'événement
      await loadEventDetails();
    } catch (err) {
      console.error('Registration error:', err);
      let errorMessage = 'Erreur lors de l\'inscription';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        // Gérer les erreurs de validation Pydantic (array d'objets)
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail?.msg) {
          errorMessage = errorData.detail.msg;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      error(errorMessage);
    } finally {
      setRegistering(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Événement introuvable</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  // Si l'événement est privé, rediriger vers la page de connexion
  if (!event.is_public) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Événement privé</h2>
            <p className="text-gray-600 mb-6">Cet événement est réservé aux membres. Veuillez vous connecter pour y accéder.</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium shadow-lg hover:shadow-xl"
            >
              Se connecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isEventPast = new Date(event.start_date) < new Date();
  const canRegister = !isEventPast && event.registration_open;

  return (
    <div className="min-h-screen bg-white">
      {/* Header avec logo/nom de l'organisation */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="text-xl font-bold text-indigo-600 hover:text-indigo-700 transition"
            >
              ← Retour
            </button>
            <h1 className="text-xl font-bold text-indigo-600">
              {getResolvedPortalTitle(settings)}
            </h1>
            {isAuthenticated ? (
              <button
                onClick={() => navigate('/calendar')}
                className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Tableau de bord
              </button>
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm text-gray-700 hover:text-gray-900 font-medium"
                >
                  Connexion
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="text-sm bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 font-medium"
                >
                  Inscription
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        {/* Hero Section avec affiche - Style inspiré de l'image */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8 relative">
          <div className="relative">
            {/* Affiche en arrière-plan avec overlay */}
            {event.poster_url && (
              <div className="relative h-[500px] md:h-[600px] overflow-hidden">
                <img 
                  src={BACKEND_URL ? `${BACKEND_URL}${event.poster_url}` : event.poster_url}
                  alt={event.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                
                {/* Contenu superposé sur l'image */}
                <div className="absolute inset-0 flex flex-col justify-between p-6 md:p-10 text-white">
                  <div>
                    <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">{event.title}</h1>
                    {event.organizer && (
                      <p className="text-lg md:text-xl text-gray-200 mb-4">Par {event.organizer.name}</p>
                    )}
                    {event.location && (
                      <div className="flex items-start gap-2 text-gray-200">
                        <svg className="h-5 w-5 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        <p className="text-base md:text-lg">{event.location}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Carte Date & Time à droite (si pas d'affiche, afficher en dessous) */}
            <div className={`${event.poster_url ? 'absolute top-6 right-6 w-full max-w-sm' : 'p-6'} bg-white rounded-xl shadow-2xl border border-gray-200`}>
              <div className="p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Date & Heure</h2>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <svg className="h-5 w-5 text-indigo-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm text-gray-600">Date</p>
                      <p className="font-semibold text-gray-900">{formatDate(event.start_date)}</p>
                      {event.end_date && (
                        <p className="text-xs text-gray-500 mt-1">jusqu'au {formatDate(event.end_date)}</p>
                      )}
                    </div>
                  </div>
                  {event.location && !event.poster_url && (
                    <div className="flex items-center gap-3">
                      <svg className="h-5 w-5 text-indigo-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="text-sm text-gray-600">Lieu</p>
                        <p className="font-semibold text-gray-900">{event.location}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Bouton d'inscription */}
                {canRegister && !showRegistrationForm && (
                  <button
                    onClick={() => setShowRegistrationForm(true)}
                    className={`w-full px-6 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition transform hover:scale-105 ${
                      event.is_free 
                        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700' 
                        : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-600 hover:to-orange-600'
                    }`}
                  >
                    {event.is_free ? 'S\'inscrire (Gratuit)' : `Réserver (${event.price} BIF)`}
                  </button>
                )}
                
                {!event.registration_open && (
                  <div className="w-full px-6 py-4 bg-gray-100 text-gray-600 rounded-xl text-center font-medium">
                    Inscriptions fermées
                  </div>
                )}
                
                {isEventPast && (
                  <div className="w-full px-6 py-4 bg-gray-100 text-gray-600 rounded-xl text-center font-medium">
                    Événement terminé
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Contenu principal en deux colonnes */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Colonne principale (gauche) */}
          <div className="lg:col-span-2 space-y-8">

            {/* Description */}
            {event.description && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Description</h2>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{event.description}</p>
              </div>
            )}

            {/* Formulaire d'inscription */}
            {canRegister && showRegistrationForm && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Formulaire d'inscription</h3>
                      <form onSubmit={handleRegister} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                            <input
                              type="text"
                              value={registrationData.name}
                              onChange={(e) => setRegistrationData({ ...registrationData, name: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                            <input
                              type="text"
                              value={registrationData.first_name}
                              onChange={(e) => setRegistrationData({ ...registrationData, first_name: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Numéro WhatsApp *</label>
                          <input
                            type="tel"
                            value={registrationData.whatsapp}
                            onChange={(e) => setRegistrationData({ ...registrationData, whatsapp: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            required
                            placeholder="+257 XX XX XX XX"
                          />
                        </div>

                        {!event.is_free && (
                          <>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Méthode de paiement *</label>
                              <select
                                value={registrationData.payment_method}
                                onChange={(e) => setRegistrationData({ ...registrationData, payment_method: e.target.value })}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                required
                              >
                                <option value="">Sélectionnez une méthode</option>
                                {paymentMethods.map(method => (
                                  <option key={method} value={method}>{method}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Preuve de paiement *</label>
                              <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                  <label className="flex-1 cursor-pointer">
                                    <input
                                      type="file"
                                      accept="image/*,.pdf"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                          handlePaymentProofUpload(file);
                                        }
                                      }}
                                      disabled={uploadingProof}
                                      className="hidden"
                                    />
                                    <div className="px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 transition text-center">
                                      {uploadingProof ? (
                                        <div className="flex items-center justify-center gap-2 text-gray-600">
                                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                                          <span>Upload en cours...</span>
                                        </div>
                                      ) : (
                                        <div className="flex items-center justify-center gap-2 text-gray-600">
                                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                          </svg>
                                          <span className="font-medium">Télécharger une photo ou un PDF</span>
                                        </div>
                                      )}
                                    </div>
                                  </label>
                                </div>
                                {paymentProofPreview && (
                                  <div className="mt-3">
                                    <p className="text-sm text-gray-600 mb-2">Aperçu:</p>
                                    <img 
                                      src={paymentProofPreview} 
                                      alt="Aperçu de la preuve de paiement"
                                      className="max-w-full h-48 object-contain border border-gray-300 rounded-lg"
                                    />
                                  </div>
                                )}
                                {registrationData.payment_proof_url && !paymentProofPreview && (
                                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <div className="flex items-center gap-2 text-green-800">
                                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                      <span className="text-sm font-medium">Fichier uploadé avec succès</span>
                                    </div>
                                    <a
                                      href={`${BACKEND_URL}${registrationData.payment_proof_url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-sm text-indigo-600 hover:text-indigo-800 mt-2 inline-flex items-center gap-1"
                                    >
                                      Voir le fichier
                                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                      </svg>
                                    </a>
                                  </div>
                                )}
                                <p className="text-xs text-gray-500">Formats acceptés: JPG, PNG, GIF, WebP, PDF (max 10MB)</p>
                              </div>
                            </div>
                          </>
                        )}

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowRegistrationForm(false);
                      setRegistrationData({
                        name: '',
                        first_name: '',
                        whatsapp: '',
                        payment_method: '',
                        payment_proof_url: ''
                      });
                      setPaymentProofFile(null);
                      setPaymentProofPreview(null);
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={registering}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {registering ? 'Inscription en cours...' : 'Confirmer l\'inscription'}
                  </button>
                </div>
              </form>
            </div>
          )}
          </div>

          {/* Sidebar (droite) */}
          <div className="lg:col-span-1 space-y-6">
            {/* Event Location */}
            {event.location && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Lieu de l'événement</h3>
                <p className="text-gray-900 font-semibold mb-2">{event.title}</p>
                <p className="text-gray-600 text-sm">{event.location}</p>
                <button className="mt-4 text-sm text-indigo-600 hover:text-indigo-700 font-medium inline-flex items-center gap-1">
                  Voir sur la carte
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </button>
              </div>
            )}

            {/* Organisateur */}
            {event.organizer && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Organisateur</h3>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                    {event.organizer.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{event.organizer.name}</p>
                    {event.organizer.email && (
                      <p className="text-sm text-gray-600">{event.organizer.email}</p>
                    )}
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Comment contacter l'organisateur ?</h4>
                  <p className="text-sm text-gray-600">
                    {event.organizer.email ? (
                      <>Envoyez un email à <a href={`mailto:${event.organizer.email}`} className="text-indigo-600 hover:text-indigo-700">{event.organizer.email}</a></>
                    ) : (
                      'Contactez l\'organisateur via les informations fournies.'
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Share */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Partager avec des amis</h3>
              <div className="flex gap-3">
                <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium flex items-center justify-center gap-2">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </button>
                <button className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition font-medium flex items-center justify-center gap-2">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  LinkedIn
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicEventDetails;

